import styles from "./FindAccount.module.css";
import { Color, path } from "../common/Common";
import { ChangeEvent, useEffect, useRef, useState, BaseSyntheticEvent } from "react";

import { useModal } from "../modal/Modal";
import ModalButtons from "../modal/ModalButtons";
import LoadingSpinner from "../common/LoadingSpinner";
import LoginForm from "./LoginForm";

import { UserIdReqBody } from "../../data_provider/gateways/UserGateway";
import { AccountValidator } from "../../workers/AccountValidator";
import DataProvider from "../../data_provider/DataProvider";
import SignUpModal from "./SignUpModal";

/**
 * @author kmy
 * @date 2025-03-17
 * @remark 아이디/비밀번호 찾기
 * @returns <section>
 */

function FindAccount() {
    const [activeTab, setActiveTab] = useState(0); // 활성 탭 인덱스

    // 25.03.24 | Kim Young Jun | 모달 State 수정
    const { ModalComponent, openModal, closeModal } = useModal();
    const [isLoading, setIsLoading] = useState(false);

    const tabs = [
        { id: 0, title: "아이디 찾기" },
        { id: 1, title: "비밀번호 찾기" },
    ];

    const handleTabClick = (index: number) => {
        setActiveTab(index);
    };

    const [findAccountInfo, setdintAccountInfo] = useState({
        id: "",
        email: "",
        name: "",
        year: "",
        month: "",
        day: "",
        gender: "male",
    });

    const [isValidName, setIsValidName] = useState(true);
    const [isValidDomain, setIsValidDomain] = useState(true);

    const showModal = (message: string) => {
        openModal({
            title: "안내",
            children: message,
            modalButtons: (
                <ModalButtons
                    buttonCount={1}
                    buttons={[
                        {
                            label: "확인",
                            color: "Primary",
                            onClick: () => closeModal(),
                        },
                    ]}
                />
            ),
        });
    };

    const handleFormChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        var key = e.target.name;
        var value = e.target.value;

        if (key == "email") {
            setIsValidDomain(validateDomain(value)); // 이메일 유효성 검사
        } else if (key == "name") {
            setIsValidName(validateName(value)); // 비밀번호 유효성 검사
        }

        setdintAccountInfo({
            ...findAccountInfo,
            [key]: value,
        });
    };

    // 이름 유효성 검사
    function validateName(name: string) {
        // 이름 길이: 2~20자, 한글, 알파벳만 허용(공백 제외)
        const regex = /^[A-Za-z가-힣]{2,20}$/;
        return regex.test(name);
    }

    // 이메일 유효성 검사
    function validateDomain(domain: string) {
        const regex = /^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        return regex.test(domain);
    }

    // 25.08.28 | Kim Young Jun | platform 값에 따른 소셜 플랫폼 이름 매핑 함수
    const getSocialPlatformName = (platform: number): string => {
        switch (platform) {
            case 1:
                return "일반";
            case 2:
                return "구글";
            case 3:
                return "네이버";
            case 4:
                return "카카오";
            case 5:
                return "페이스북";
            case 6:
                return "애플";
            default:
                return "소셜";
        }
    };

    // 25.03.24 | Kim Young Jun | 아이디 찾기 버튼 클릭 시, POST Request 진행

    const findIdBtnClicked = async (e: BaseSyntheticEvent) => {
        e.preventDefault(); // 기본 이벤트 방지

        const { name, year, month, day, gender } = findAccountInfo;

        // 생년월일 유효성 검사
        if (!name || !year || !month || !day) {
            showModal("이름과 생년월일을 모두 입력해주세요.");
            return;
        }

        // API 요청을 위한 데이터 객체 생성
        const requestBody = {
            name: name,
            birth: `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`,
            sex: gender === "male" ? "1" : "0",
        } as UserIdReqBody;

        try {
            const response = await DataProvider.GetUserId(requestBody);

            if (response.flag && response.result.userid) {
                showModal(`회원님의 아이디는\n${response.result.userid}\n입니다.`);
            } else {
                showModal("아이디를 찾을 수 없습니다.\n정보를 올바르게 입력했는지 확인해주세요.");
            }
        } catch (error) {
            showModal(error instanceof Error ? error.message : "오류가 발생했습니다.");
        }
    };

    // 25.03.24 | Kim Young Jun | 비밀번호 찾기 버튼 클릭 시, POST Request 진행
    // 25.08.27 | Kim Young Jun | 먼저 해당 계정 유무 조회 후, 소셜 계정일 때는 모달 다르게 띄울 수 있도록 수정
    const findPasswordBtnClicked = async (e: BaseSyntheticEvent) => {
        e.preventDefault(); // 기본 이벤트 방지

        const { id, email } = findAccountInfo;

        // userid 입력 유효성 검사
        if (!id || !email || !validateDomain(email)) {
            showModal("이메일을 올바르게 입력해주세요.");
            return;
        }

        openModal({
            title: "계정을 확인중입니다...",
            children: <LoadingSpinner />,
        });

        setIsLoading(true);

        try {
            // 먼저 계정 유무 확인
            const userCheckResult = await DataProvider.GetUserNotLogInInfo({
                address: id,
                host: email,
            });

            // 1초 delay 추가
            await new Promise((resolve) => setTimeout(resolve, 1000));

            if (!userCheckResult.flag) {
                openModal({
                    title: "안내",
                    children: "존재하지 않는 아이디입니다.",
                    modalButtons: (
                        <ModalButtons
                            buttonCount={1}
                            buttons={[
                                {
                                    label: "확인",
                                    color: "Primary",
                                    onClick: () => closeModal(),
                                },
                            ]}
                        />
                    ),
                });
                return;
            }

            // 계정이 존재하면 platform 확인
            const platform = userCheckResult.result?.platform;

            console.log("platform : ", platform);

            // 소셜 계정인 경우 (platform이 1이 아닌 경우)
            if (platform && platform !== 1) {
                const socialPlatformName = getSocialPlatformName(platform);

                openModal({
                    title: "안내",
                    children: `해당 아이디는 소셜 계정(${socialPlatformName})으로 가입한 계정입니다. 소셜 로그인을 진행 해주세요.`,
                    modalButtons: (
                        <ModalButtons
                            buttonCount={2}
                            buttons={[
                                {
                                    label: "취소",
                                    color: "Secondary",
                                    onClick: () => closeModal(),
                                },
                                {
                                    label: "로그인",
                                    color: "Primary",
                                    onClick: () => {
                                        // 로그인 모달 띄우기
                                        openModal({
                                            title: "로그인",
                                            children: <LoginForm onClose={closeModal} onClickSignUp={onClickSignUp} />,
                                            modalButtons: null,
                                            size: "wide",
                                            disableOverlayClose: true,
                                        });
                                    },
                                },
                            ]}
                        />
                    ),
                });
                return;
            }

            // 일반 계정인 경우 비밀번호 찾기 메일 발송
            openModal({
                title: "메일을 발송중입니다...",
                children: <LoadingSpinner />,
            });

            const validator = new AccountValidator();
            const address = id;
            const host = email;

            const resultContents = await validator.findPasswordByEmail(address, host);

            // 메일 발송 후 1초 delay 추가
            await new Promise((resolve) => setTimeout(resolve, 1000));

            openModal({
                title: "알림",
                children: resultContents,
                modalButtons: (
                    <ModalButtons
                        buttonCount={1}
                        buttons={[
                            {
                                label: "확인",
                                color: "Primary",
                                onClick: () => closeModal(),
                            },
                        ]}
                    />
                ),
            });
        } catch (error) {
            showModal(error instanceof Error ? error.message : "오류가 발생했습니다.");
        } finally {
            setIsLoading(false);
        }
    };

    const onClickSignUp = () => {
        openModal({
            title: "회원가입",
            children: <SignUpModal onClose={closeModal} />,
            modalButtons: null,
            size: "normal",
        });
    };

    return (
        <div className={styles.wrapper}>
            <ModalComponent />

            <div className={styles.tit_bx}>
                <h2 className={[styles.tit, styles.blue].join(" ")}>아이디 / 비밀번호 찾기</h2>
            </div>

            <div className={styles.tabs_container}>
                {/* 탭 메뉴 */}
                <ul className={styles.tabs}>
                    {tabs.map((tab, index) => (
                        <li key={tab.id} className={`${activeTab == index ? styles.active : ""}`} onClick={() => handleTabClick(index)}>
                            {tab.title}
                        </li>
                    ))}
                </ul>

                <div className={styles.tab_content}>
                    {/* 아이디 찾기 */}
                    {activeTab == 0 && (
                        <div className={styles.tab_panel}>
                            <div className={styles.regist_bx}>
                                <div className={`${styles.container} ${styles.row}`}>
                                    <div className={styles.info_tit_bx}>
                                        <p className={styles.info_tit}>
                                            이름 <span className={styles.required}>*</span>
                                        </p>
                                    </div>
                                    <div className={`${styles.row} ${styles.feedback_container}`}>
                                        <input
                                            className={`${styles.form_control} ${!isValidName ? styles.invalid : ""}`}
                                            name="name"
                                            placeholder={"이름"}
                                            value={findAccountInfo.name}
                                            onChange={handleFormChange}
                                        />
                                        {isValidName == false && <span>이름은 공백을 제외한 한글과 알파벳만 허용됩니다.</span>}
                                    </div>
                                    <div className={`${styles.btn_group} ${styles.row}`} style={{ width: "25%" }} role="group">
                                        <input
                                            type="radio"
                                            className={styles.btn_check}
                                            name="gender"
                                            id="male"
                                            value="male"
                                            checked={findAccountInfo.gender == "male"}
                                            onChange={handleFormChange}
                                        />
                                        <label htmlFor="male">남</label>
                                        <input
                                            type="radio"
                                            className={styles.btn_check}
                                            name="gender"
                                            id="female"
                                            value="female"
                                            checked={findAccountInfo.gender == "female"}
                                            onChange={handleFormChange}
                                        />
                                        <label htmlFor="female">여</label>
                                    </div>
                                </div>
                                <div className={`${styles.container} ${styles.row}`}>
                                    <div className={styles.info_tit_bx}>
                                        <p className={styles.info_tit}>
                                            생년월일 <span className={styles.required}>*</span>
                                        </p>
                                    </div>
                                    <div className={`${styles.row} ${styles.birth_container}`}>
                                        <select name="year" onChange={handleFormChange}>
                                            <option value="" disabled selected>
                                                년
                                            </option>
                                            {Array.from({ length: new Date().getFullYear() - 1900 + 1 }, (_, index) => 1900 + index)
                                                .reverse()
                                                .map((date) => (
                                                    <option key={date} value={date}>
                                                        {date}
                                                    </option>
                                                ))}
                                        </select>
                                        <select name="month" onChange={handleFormChange}>
                                            <option value="" disabled selected>
                                                월
                                            </option>
                                            {Array.from({ length: 12 }, (_, index) => index + 1).map((date) => (
                                                <option key={date} value={date}>
                                                    {date}
                                                </option>
                                            ))}
                                        </select>
                                        <select name="day" onChange={handleFormChange}>
                                            <option value="" disabled selected>
                                                일
                                            </option>
                                            {Array.from({ length: 31 }, (_, index) => index + 1).map((date) => (
                                                <option key={date} value={date}>
                                                    {date}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div style={{ width: "25%" }} />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* 비밀번호 찾기 */}
                    {activeTab == 1 && (
                        <div className={styles.tab_panel}>
                            <div className={styles.regist_bx}>
                                <div className={`${styles.container} ${styles.row}`}>
                                    <div className={styles.info_tit_bx}>
                                        <p className={styles.info_tit}>
                                            아이디 <span className={styles.required}>*</span>
                                        </p>
                                    </div>
                                    <div className={`${styles.row}`}>
                                        <div className={`${styles.row} ${styles.feedback_container}`}>
                                            <input
                                                className={styles.form_control}
                                                name="id"
                                                placeholder={"아이디"}
                                                value={findAccountInfo.id}
                                                onChange={handleFormChange}
                                            />
                                        </div>

                                        <div className={`${styles.row} ${styles.feedback_container}`}>
                                            <div className={styles.rowWithEmail}>
                                                <p>@</p>
                                                <input
                                                    className={`${styles.form_control} ${!isValidDomain ? styles.invalid : ""}`}
                                                    name="email"
                                                    placeholder={"이메일"}
                                                    value={findAccountInfo.email}
                                                    onChange={handleFormChange}
                                                />
                                            </div>
                                            {isValidDomain == false && <span>이메일 형식을 확인해주세요.</span>}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className={`${styles.find_btn}`} style={{ marginTop: "30px" }}>
                {activeTab == 0 && <button onClick={findIdBtnClicked}>아이디 찾기</button>}
                {activeTab == 1 && <button onClick={findPasswordBtnClicked}>비밀번호 찾기</button>}
            </div>
        </div>
    );
}

export default FindAccount;
